import { AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';
import { AdminSectionCard } from '../primitives/admin-section-card.js';
import { AdminSkeleton } from '../primitives/admin-skeleton.js';
import { AdminStatusBadge } from '../primitives/admin-status-badge.js';
import { formatCount, formatDateTime } from '../../lib/format.js';
import type {
  NotificationsDto,
  PaymentsDto,
  RewardsDto,
  SystemHealthDto,
} from '../../types/dashboard.js';

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}

export interface PaymentsSummaryCardProps {
  payments: PaymentsDto | undefined;
  isLoading: boolean;
}

/** Compact operational summary -- future navigation hook to the full payments-operations page (Milestone 2), not implemented here (Part 11). */
export function PaymentsSummaryCard({ payments, isLoading }: PaymentsSummaryCardProps) {
  return (
    <AdminSectionCard title="Payments" description="Status breakdown for the selected period" headingLevel="h3">
      {isLoading || !payments ? (
        <AdminSkeleton lines={3} className="h-5" />
      ) : (
        <div className="divide-y divide-gray-50">
          <SummaryRow label="Captured" value={formatCount(payments.byStatus.captured ?? 0)} />
          <SummaryRow label="Pending" value={formatCount(payments.byStatus.pending ?? 0)} />
          <SummaryRow label="Failed" value={formatCount(payments.byStatus.failed ?? 0)} />
          <SummaryRow label="Refunded" value={formatCount(payments.byStatus.refunded ?? 0)} />
          <SummaryRow label="Partially refunded" value={formatCount(payments.byStatus.partially_refunded ?? 0)} />
        </div>
      )}
    </AdminSectionCard>
  );
}

export interface RewardsSummaryCardProps {
  rewards: RewardsDto | undefined;
  isLoading: boolean;
}

export function RewardsSummaryCard({ rewards, isLoading }: RewardsSummaryCardProps) {
  return (
    <AdminSectionCard title="Rewards" description="Spin-wheel activity for the selected period" headingLevel="h3">
      {isLoading || !rewards ? (
        <AdminSkeleton lines={3} className="h-5" />
      ) : (
        <div className="divide-y divide-gray-50">
          <SummaryRow label="Spins" value={formatCount(rewards.spins)} />
          <SummaryRow label="Winning spins" value={formatCount(rewards.winningSpins)} />
          <SummaryRow label="No-win outcomes" value={formatCount(rewards.noWinSpins)} />
          <SummaryRow label="Coupons issued" value={formatCount(rewards.couponsIssued)} />
          <SummaryRow label="Coupons redeemed" value={formatCount(rewards.couponsRedeemed)} />
        </div>
      )}
    </AdminSectionCard>
  );
}

export interface NotificationsSummaryCardProps {
  notifications: NotificationsDto | undefined;
  isLoading: boolean;
}

export function NotificationsSummaryCard({ notifications, isLoading }: NotificationsSummaryCardProps) {
  return (
    <AdminSectionCard title="Notifications" description="Delivery status for the selected period" headingLevel="h3">
      {isLoading || !notifications ? (
        <AdminSkeleton lines={3} className="h-5" />
      ) : (
        <div className="divide-y divide-gray-50">
          <SummaryRow label="Sent" value={formatCount(notifications.sent)} />
          <SummaryRow label="Delivered" value={formatCount(notifications.delivered)} />
          <SummaryRow label="Failed" value={formatCount(notifications.failed)} />
          <SummaryRow label="Pending now" value={formatCount(notifications.pendingNow)} />
          <SummaryRow label="Retry jobs due now" value={formatCount(notifications.retryJobsDueNow)} />
        </div>
      )}
    </AdminSectionCard>
  );
}

export interface SystemHealthSummaryCardProps {
  systemHealth: SystemHealthDto | undefined;
  isLoading: boolean;
}

/** Only reliable, already-collected indicators -- never a fabricated uptime/latency figure the system doesn't actually measure (Part 12). "Unknown" is shown explicitly rather than guessed. */
export function SystemHealthSummaryCard({ systemHealth, isLoading }: SystemHealthSummaryCardProps) {
  const tone =
    systemHealth?.status === 'ok' ? 'success' : systemHealth?.status === 'degraded' ? 'warning' : 'neutral';
  const Icon = systemHealth?.status === 'ok' ? CheckCircle2 : systemHealth?.status === 'degraded' ? AlertTriangle : HelpCircle;

  return (
    <AdminSectionCard title="System health" description="Job and webhook reliability for the selected period" headingLevel="h3">
      {isLoading || !systemHealth ? (
        <AdminSkeleton lines={3} className="h-5" />
      ) : (
        <>
          <div className="mb-2 flex items-center gap-2">
            <Icon className="h-4 w-4 text-gray-400" aria-hidden="true" />
            <AdminStatusBadge
              label={systemHealth.status === 'unknown' ? 'Unknown' : systemHealth.status}
              tone={tone}
            />
          </div>
          <div className="divide-y divide-gray-50">
            <SummaryRow label="Failed jobs" value={formatCount(systemHealth.failedJobsInPeriod)} />
            <SummaryRow label="Webhook failures" value={formatCount(systemHealth.webhookFailuresInPeriod)} />
            <SummaryRow
              label="Last successful job"
              value={systemHealth.lastSuccessfulJobAt ? formatDateTime(systemHealth.lastSuccessfulJobAt) : 'Unknown'}
            />
          </div>
        </>
      )}
    </AdminSectionCard>
  );
}
